/**
 * ESC/POS Command Builder for Thermal Printers
 * 
 * Builds raw ESC/POS commands for 80mm/58mm thermal receipt printers.
 * Compatible with Epson TM series, POS-58, and most ESC/POS printers.
 * 
 * Usage:
 *   const builder = new ESCPOSBuilder();
 *   builder.initialize().center().bold().text('TITLE').cut();
 *   const commands = builder.build();
 */

// ESC/POS Command Constants
const ESC = '\x1B';
const GS = '\x1D';
const LF = '\x0A';

// Character Sets
const CODEPAGE = {
  PC437: 0,      // USA Standard
  PC850: 2,      // Multilingual
  PC860: 3,      // Portuguese
  PC863: 4,      // Canadian-French
  PC865: 5,      // Nordic
  WPC1252: 16,   // Latin 1
  CP866: 17,     // Cyrillic #2
  CP852: 18,     // Latin 2
  CP858: 19,     // Euro
};

class ESCPOSBuilder {
  constructor(options = {}) {
    this.commands = [];
    this.encoding = options.encoding || 'cp437';
    this.paperWidth = options.paperWidth || 80; // 80mm or 58mm
    this.charsPerLine = this.paperWidth === 80 ? 48 : 32;
  }

  // ============ INITIALIZATION ============

  /**
   * Initialize printer - clears buffer and resets settings
   */
  initialize() {
    this.commands.push(ESC + '@');
    return this;
  }

  /**
   * Set character code page
   */
  setCodePage(page = CODEPAGE.PC437) {
    this.commands.push(ESC + 't' + String.fromCharCode(page));
    return this;
  }

  // ============ TEXT FORMATTING ============

  /**
   * Set text alignment
   * @param {string} align - 'left', 'center', 'right'
   */
  align(align) {
    const alignCodes = { left: 0, center: 1, right: 2 };
    this.commands.push(ESC + 'a' + String.fromCharCode(alignCodes[align] || 0));
    return this;
  }

  left() { return this.align('left'); }
  center() { return this.align('center'); }
  right() { return this.align('right'); }

  /**
   * Enable/disable bold text
   */
  bold(enable = true) {
    this.commands.push(ESC + 'E' + String.fromCharCode(enable ? 1 : 0));
    return this;
  }

  boldOff() { return this.bold(false); }

  /**
   * Enable/disable underline
   * @param {number} mode - 0: off, 1: 1-dot, 2: 2-dot
   */
  underline(mode = 1) {
    this.commands.push(ESC + '-' + String.fromCharCode(mode));
    return this;
  }

  underlineOff() { return this.underline(0); }

  /**
   * Set text size
   * @param {number} width - 1-8 (1 = normal)
   * @param {number} height - 1-8 (1 = normal)
   */
  textSize(width = 1, height = 1) {
    const w = Math.min(Math.max(width, 1), 8) - 1;
    const h = Math.min(Math.max(height, 1), 8) - 1;
    this.commands.push(GS + '!' + String.fromCharCode((w << 4) | h));
    return this;
  }

  normal() { return this.textSize(1, 1); }
  doubleHeight() { return this.textSize(1, 2); }
  doubleWidth() { return this.textSize(2, 1); }
  doubleSize() { return this.textSize(2, 2); }

  /**
   * Invert colors (white on black)
   */
  invert(enable = true) {
    this.commands.push(GS + 'B' + String.fromCharCode(enable ? 1 : 0));
    return this;
  }

  invertOff() { return this.invert(false); }

  // ============ TEXT OUTPUT ============

  /**
   * Add text
   */
  text(str) {
    this.commands.push(str);
    return this;
  }

  /**
   * Add text with newline
   */
  textLn(str = '') {
    this.commands.push(str + LF);
    return this;
  }

  /**
   * Add empty line(s)
   */
  newLine(count = 1) {
    for (let i = 0; i < count; i++) {
      this.commands.push(LF);
    }
    return this;
  }

  /**
   * Print horizontal separator line
   * @param {string} char - Character to use
   */
  separator(char = '-') {
    this.commands.push(char.repeat(this.charsPerLine) + LF);
    return this;
  }

  /**
   * Print double line separator
   */
  doubleSeparator() {
    return this.separator('=');
  }

  /**
   * Print two columns (left and right aligned)
   * @param {string} left - Left text
   * @param {string} right - Right text
   */
  twoColumns(left, right) {
    const maxLeft = this.charsPerLine - right.length - 1;
    const leftPadded = left.substring(0, maxLeft).padEnd(maxLeft, ' ');
    this.commands.push(leftPadded + ' ' + right + LF);
    return this;
  }

  /**
   * Print three columns
   */
  threeColumns(left, center, right) {
    const colWidth = Math.floor(this.charsPerLine / 3);
    const l = left.substring(0, colWidth).padEnd(colWidth, ' ');
    const c = center.substring(0, colWidth).padStart(Math.floor(colWidth / 2) + Math.floor(center.length / 2)).padEnd(colWidth, ' ');
    const r = right.substring(0, colWidth).padStart(colWidth, ' ');
    this.commands.push(l + c + r + LF);
    return this;
  }

  /**
   * Print item line (name, qty, price)
   */
  itemLine(name, qty, price) {
    const priceStr = price.toString();
    const qtyStr = `x${qty}`;
    const maxName = this.charsPerLine - priceStr.length - qtyStr.length - 2;
    const nameStr = name.substring(0, maxName).padEnd(maxName, ' ');
    this.commands.push(nameStr + ' ' + qtyStr.padStart(3, ' ') + ' ' + priceStr + LF);
    return this;
  }

  // ============ BARCODE & QR CODE ============

  /**
   * Print QR Code
   * @param {string} data - Data to encode
   * @param {number} size - Module size (1-16)
   */
  qrCode(data, size = 4) {
    const moduleSize = Math.min(Math.max(size, 1), 16);
    
    // QR Code model
    this.commands.push(GS + '(k' + '\x04\x00' + '\x31\x41' + '\x32\x00');
    
    // QR Code size
    this.commands.push(GS + '(k' + '\x03\x00' + '\x31\x43' + String.fromCharCode(moduleSize));
    
    // Error correction level (L=48, M=49, Q=50, H=51)
    this.commands.push(GS + '(k' + '\x03\x00' + '\x31\x45' + '\x31');
    
    // Store data
    const len = data.length + 3;
    const pL = len % 256;
    const pH = Math.floor(len / 256);
    this.commands.push(GS + '(k' + String.fromCharCode(pL) + String.fromCharCode(pH) + '\x31\x50\x30' + data);
    
    // Print QR code
    this.commands.push(GS + '(k' + '\x03\x00' + '\x31\x51\x30');
    
    return this;
  }

  /**
   * Print barcode
   * @param {string} data - Barcode data
   * @param {string} type - 'CODE39', 'CODE128', 'EAN13', 'EAN8', 'UPC-A'
   */
  barcode(data, type = 'CODE128') {
    const types = {
      'UPC-A': 65,
      'UPC-E': 66,
      'EAN13': 67,
      'EAN8': 68,
      'CODE39': 69,
      'CODE128': 73,
    };
    
    const typeCode = types[type] || types['CODE128'];
    
    // Set barcode height (50 dots)
    this.commands.push(GS + 'h' + String.fromCharCode(50));
    
    // Set barcode width (2 = medium)
    this.commands.push(GS + 'w' + String.fromCharCode(2));
    
    // Print position of HRI (Human Readable Interpretation)
    this.commands.push(GS + 'H' + String.fromCharCode(2)); // Below barcode
    
    // Print barcode
    this.commands.push(GS + 'k' + String.fromCharCode(typeCode) + String.fromCharCode(data.length) + data);
    
    return this;
  }

  // ============ PAPER CONTROL ============

  /**
   * Feed paper
   * @param {number} lines - Number of lines to feed
   */
  feed(lines = 1) {
    this.commands.push(ESC + 'd' + String.fromCharCode(lines));
    return this;
  }

  /**
   * Cut paper
   * @param {string} mode - 'full' or 'partial'
   */
  cut(mode = 'partial') {
    this.feed(3); // Feed before cut
    this.commands.push(GS + 'V' + String.fromCharCode(mode === 'full' ? 0 : 1));
    return this;
  }

  /**
   * Open cash drawer
   * @param {number} pin - 0 or 1
   */
  openDrawer(pin = 0) {
    const pinCode = pin === 0 ? 0 : 1;
    this.commands.push(ESC + 'p' + String.fromCharCode(pinCode) + '\x19\xFA');
    return this;
  }

  // ============ BUILD ============

  /**
   * Build and return the command array
   */
  build() {
    return this.commands;
  }

  /**
   * Build and return as single string
   */
  buildString() {
    return this.commands.join('');
  }

  /**
   * Reset builder for reuse
   */
  reset() {
    this.commands = [];
    return this;
  }
}

// ============ RECEIPT TEMPLATES ============

/**
 * Build receipt for customer
 */
export function buildReceipt(order, outlet, options = {}) {
  const builder = new ESCPOSBuilder({ paperWidth: options.paperWidth || 80 });
  const formatPrice = (price) => `Rp ${price.toLocaleString('id-ID')}`;
  
  builder
    .initialize()
    .setCodePage(CODEPAGE.PC437);
  
  // Header
  builder
    .center()
    .doubleSize()
    .bold()
    .textLn(outlet.name || 'RESTAURANT')
    .normal()
    .boldOff()
    .textLn(outlet.address || '')
    .textLn(outlet.phone ? `Telp: ${outlet.phone}` : '')
    .separator();
  
  // Order Info
  builder
    .left()
    .twoColumns('Order #:', order.order_number || order.id?.slice(-8))
    .twoColumns('Tanggal:', new Date(order.created_at).toLocaleDateString('id-ID'))
    .twoColumns('Waktu:', new Date(order.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }));
  
  if (order.table_name) {
    builder.twoColumns('Meja:', order.table_name);
  }
  
  if (order.customer_name) {
    builder.twoColumns('Pelanggan:', order.customer_name);
  }
  
  if (order.cashier_name) {
    builder.twoColumns('Kasir:', order.cashier_name);
  }
  
  builder.separator();
  
  // Items
  order.items?.forEach(item => {
    const itemTotal = (item.unit_price || item.price) * item.quantity;
    builder.itemLine(item.name, item.quantity, formatPrice(itemTotal));
    
    // Variants
    if (item.selected_variants?.length > 0) {
      item.selected_variants.forEach(v => {
        builder.textLn(`  + ${v.name}`);
      });
    }
    
    // Add-ons
    if (item.selected_addons?.length > 0) {
      item.selected_addons.forEach(a => {
        builder.textLn(`  + ${a.name} (${formatPrice(a.price)})`);
      });
    }
    
    // Notes
    if (item.notes) {
      builder.textLn(`  * ${item.notes}`);
    }
  });
  
  builder.separator();
  
  // Totals
  builder.twoColumns('Subtotal:', formatPrice(order.subtotal || order.total_price));
  
  if (order.discount_amount > 0) {
    builder.twoColumns('Diskon:', `-${formatPrice(order.discount_amount)}`);
  }
  
  if (order.tax_amount > 0) {
    builder.twoColumns('Pajak:', formatPrice(order.tax_amount));
  }
  
  if (order.service_charge > 0) {
    builder.twoColumns('Service:', formatPrice(order.service_charge));
  }
  
  builder
    .doubleSeparator()
    .bold()
    .doubleHeight()
    .twoColumns('TOTAL:', formatPrice(order.total_price))
    .normal()
    .boldOff()
    .doubleSeparator();
  
  // Payment Info
  if (order.payments?.length > 0) {
    builder.textLn('Pembayaran:');
    order.payments.forEach(p => {
      builder.twoColumns(`  ${p.method}:`, formatPrice(p.amount));
    });
    
    const totalPaid = order.payments.reduce((sum, p) => sum + p.amount, 0);
    const change = totalPaid - order.total_price;
    if (change > 0) {
      builder.twoColumns('Kembalian:', formatPrice(change));
    }
  }
  
  // Footer
  builder
    .newLine()
    .center()
    .textLn('Terima Kasih')
    .textLn('Selamat Menikmati!')
    .newLine();
  
  // QR Code for order tracking (optional)
  if (options.includeQR && order.id) {
    builder
      .center()
      .qrCode(`${options.baseUrl || ''}/track/${order.id}`, 4)
      .newLine();
  }
  
  builder
    .textLn(new Date().toLocaleString('id-ID'))
    .cut();
  
  return builder.build();
}

/**
 * Build kitchen ticket
 */
export function buildKitchenTicket(order, printerRole = 'KITCHEN', options = {}) {
  const builder = new ESCPOSBuilder({ paperWidth: options.paperWidth || 80 });
  
  // Filter items based on printer role
  const items = order.items?.filter(item => {
    if (printerRole === 'KITCHEN') {
      return item.menu_type === 'FOOD' || !item.menu_type;
    } else if (printerRole === 'BAR') {
      return item.menu_type === 'DRINK';
    }
    return true;
  }) || [];
  
  if (items.length === 0) {
    return null; // No items for this printer
  }
  
  builder
    .initialize()
    .setCodePage(CODEPAGE.PC437);
  
  // Header with time
  builder
    .center()
    .doubleSize()
    .bold()
    .textLn(printerRole === 'KITCHEN' ? 'DAPUR' : 'BAR')
    .normal()
    .textLn(new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' }))
    .doubleSeparator();
  
  // Order info
  builder
    .left()
    .doubleHeight()
    .bold()
    .textLn(`#${order.order_number || order.id?.slice(-8)}`)
    .normal()
    .boldOff();
  
  if (order.table_name) {
    builder.doubleSize().textLn(`MEJA: ${order.table_name}`).normal();
  }
  
  if (order.is_qr_order) {
    builder.invert().textLn(' QR ORDER ').invertOff();
  }
  
  builder.separator();
  
  // Items - large and clear
  items.forEach((item, index) => {
    builder
      .doubleHeight()
      .bold()
      .textLn(`${item.quantity}x ${item.name}`)
      .normal()
      .boldOff();
    
    // Variants
    if (item.selected_variants?.length > 0) {
      item.selected_variants.forEach(v => {
        builder.textLn(`   > ${v.name}`);
      });
    }
    
    // Add-ons
    if (item.selected_addons?.length > 0) {
      item.selected_addons.forEach(a => {
        builder.textLn(`   + ${a.name}`);
      });
    }
    
    // Notes - highlighted
    if (item.notes) {
      builder.invert().textLn(` ! ${item.notes} `).invertOff();
    }
    
    if (index < items.length - 1) {
      builder.newLine();
    }
  });
  
  // Order notes
  if (order.notes) {
    builder
      .separator()
      .bold()
      .textLn('CATATAN:')
      .boldOff()
      .textLn(order.notes);
  }
  
  builder
    .separator()
    .center()
    .textLn(order.customer_name || '-')
    .cut();
  
  return builder.build();
}

/**
 * Build test print
 */
export function buildTestPrint(printerName) {
  const builder = new ESCPOSBuilder();
  
  builder
    .initialize()
    .center()
    .doubleSeparator()
    .doubleSize()
    .bold()
    .textLn('TEST PRINT')
    .normal()
    .boldOff()
    .doubleSeparator()
    .newLine()
    .textLn('Printer berhasil terhubung!')
    .textLn(`Nama: ${printerName}`)
    .textLn(`Waktu: ${new Date().toLocaleString('id-ID')}`)
    .newLine()
    .textLn('Jika Anda melihat struk ini,')
    .textLn('printer siap digunakan.')
    .newLine()
    .doubleSeparator()
    .cut();
  
  return builder.build();
}

/**
 * Build bill/pre-receipt (before payment)
 */
export function buildBill(order, outlet, options = {}) {
  const builder = new ESCPOSBuilder({ paperWidth: options.paperWidth || 80 });
  const formatPrice = (price) => `Rp ${price.toLocaleString('id-ID')}`;
  
  builder
    .initialize()
    .center()
    .doubleSize()
    .bold()
    .textLn('TAGIHAN')
    .normal()
    .boldOff()
    .textLn(outlet.name || 'RESTAURANT')
    .separator();
  
  builder
    .left()
    .twoColumns('Order #:', order.order_number || order.id?.slice(-8))
    .twoColumns('Meja:', order.table_name || '-')
    .twoColumns('Waktu:', new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }))
    .separator();
  
  // Items
  order.items?.forEach(item => {
    const itemTotal = (item.unit_price || item.price) * item.quantity;
    builder.itemLine(item.name, item.quantity, formatPrice(itemTotal));
  });
  
  builder
    .doubleSeparator()
    .bold()
    .doubleHeight()
    .twoColumns('TOTAL:', formatPrice(order.total_price))
    .normal()
    .boldOff()
    .doubleSeparator()
    .newLine()
    .center()
    .textLn('Silakan bayar di kasir')
    .cut();
  
  return builder.build();
}

export { ESCPOSBuilder, CODEPAGE };
export default ESCPOSBuilder;
